/**
 * Publish the browser keyboard overlap as a CSS variable for full-height auth screens.
 *
 * Mobile browsers can leave the layout viewport at full height while shrinking the
 * visual viewport. CSS alone then docks a button behind the keypad, so the auth screens
 * opt into the measured difference through `KEYBOARD_INSET_VAR`.
 */

/** The variable read by the auth screens. Each use supplies its own zero fallback. */
export const KEYBOARD_INSET_VAR = "--elyxeer-keyboard-inset";

/** px — smaller viewport changes are browser chrome rather than a keyboard. */
const KEYBOARD_MIN_HEIGHT = 80;

/** Start publishing the inset and return its teardown. */
export function trackKeyboardInset(): () => void {
  const viewport = window.visualViewport;

  const apply = () => {
    // `offsetTop` accounts for a browser that already scrolled the focused field up.
    const measured = viewport
      ? window.innerHeight - viewport.height - viewport.offsetTop
      : 0;
    const inset = measured < KEYBOARD_MIN_HEIGHT ? 0 : Math.round(measured);
    document.documentElement.style.setProperty(
      KEYBOARD_INSET_VAR,
      `${inset}px`,
    );
  };

  apply();
  viewport?.addEventListener("resize", apply);
  viewport?.addEventListener("scroll", apply);
  window.addEventListener("resize", apply);

  return () => {
    viewport?.removeEventListener("resize", apply);
    viewport?.removeEventListener("scroll", apply);
    window.removeEventListener("resize", apply);
    document.documentElement.style.removeProperty(KEYBOARD_INSET_VAR);
  };
}
